import { logger } from '@/utils/log/logger';
import gqlClient from '../gql';
import {
  GET_PAGE_WITH_OPINION_FEATURE_PAGES_MENU,
  GET_PAGES,
  pagesVariables,
} from '../graphql';
import {
  Block,
  BlockElement,
  Content,
  CustomPage,
  OpinionFeaturePageMenu,
  PageResponse,
  Section,
} from '../model';

export const getTopicPage = async (
  slug: string,
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    // @ts-ignore
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        slug,
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    if (!pageData) return undefined;
    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting topic page: ${error}`);
  }
};

export const getHomePage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_home',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getHomepageWithMenu = async (
  channelId?: string,
  languageCode?: string,
): Promise<OpinionFeaturePageMenu | undefined> => {
  try {
    const opinionWhere = pagesVariables({
      pageLayoutIdentifier: 'default_layout_opinion_home',
      channelId,
      languageCode,
    }).where;
    const featureWhere = pagesVariables({
      pageLayoutIdentifier: 'default_layout_features_home',
      channelId,
      languageCode,
    }).where;
    const pageResponse = await gqlClient.query<{
      OpinionPages: { docs: { slug: string }[] };
      FeaturePages: { docs: { slug: string }[] };
    }>({
      query: GET_PAGE_WITH_OPINION_FEATURE_PAGES_MENU,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: {
        opinionWhere,
        featureWhere,
        locale: languageCode,
      },
    });
    const pageData = pageResponse?.data;
    const featurePage = pageData?.FeaturePages.docs[0];
    const opinionPage = pageData?.OpinionPages.docs[0];
    const menuData = {
      featurePage: featurePage,
      opinionPage: opinionPage,
    };

    return { menuData };
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getOpinionDetailPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_opinion',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getFeaturesPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_features',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getFeaturesDetailPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_features',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getSectionFromPage = (
  page: CustomPage | undefined,
  identifier: Section['sectionLayoutIdentifier'],
): Section | undefined => {
  if (!page?.sections) return;
  const section = page?.sections?.find(section => {
    const { sectionLayoutIdentifier } = section as Section;
    return sectionLayoutIdentifier === identifier;
  }) as Section;

  if (!section?.blocks?.length) return;
  section.blocks = section?.blocks?.filter(
    block =>
      (block as Block).blockLayoutIdentifier === 'all_tv_shows' ||
      (block as Block).blockLayoutIdentifier === 'live_tv_header' ||
      (block as Block).blockLayoutIdentifier === 'latest_tv_show_contents' ||
      ((block as Block).blockLayoutIdentifier === 'listen_radio_large' &&
        (block as Block).blockElements?.filter(b => b.radioPlaylist).length) ||
      (block as Block).blockElements?.some(
        element =>
          (element as BlockElement).liveCoverage?.liveCoverageRecords?.length ||
          (element as BlockElement).coverMedia?.length ||
          (element as BlockElement).tvShow ||
          (element as BlockElement).liveStream?.streamUrl ||
          (element as BlockElement).content,
      ),
  ) as Block[];
  if (!section?.blocks?.length) return;
  return section;
};

export const getBlockFromSection = (
  section: Section | undefined,
  identifier: Block['blockLayoutIdentifier'],
): Block | undefined => {
  return section?.blocks?.find(block => {
    const { blockLayoutIdentifier } = block as Block;
    return blockLayoutIdentifier === identifier;
  }) as Block | undefined;
};

export const getBlocksFromSection = (
  section: Section | undefined | null,
): Block[] | undefined => {
  return section?.blocks?.filter(block => {
    return typeof block === 'object';
  });
};

export const getContentsFromBlock = (item?: Block): Content[] | undefined => {
  const elements = item?.blockElements as BlockElement[] | undefined;
  return elements
    ?.filter(element => !!element.content)
    .map(element => {
      return element.content as Content;
    });
};

export const getContentsFromBlocks = (
  blocks?: Block[] | null | undefined,
): Content[] | undefined => {
  return blocks?.reduce<Content[]>(
    (acc, item) => [...acc, ...getContentsFromBlock(item)!],
    [],
  );
};

export const getContentIdsFromSections = (
  sections: (Section | undefined)[],
) => {
  return sections
    .filter(section => !!section)
    .map(section => {
      const blocks = getBlocksFromSection(section);
      return getContentsFromBlocks(blocks)?.map(content => content.id) ?? [];
    })
    .reduce((all, nextArr) => {
      return [...all, ...nextArr];
    }, [] as string[]);
};

export const getTVLandingPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_video',
        pageLayoutIdentifier: 'default_layout_video_home',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getDocumentariesPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_documentaries',
        pageLayoutIdentifier: 'default_layout_documentaries_home',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getShowPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_tv_show',
        pageLayoutIdentifier: 'default_layout_tv_show_home',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getShowsPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_tv_shows',
        pageLayoutIdentifier: 'default_layout_video_show_list',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getDigitalPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_digital',
        pageLayoutIdentifier: 'default_layout_digital_home',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};

export const getListenPage = async (
  channelId?: string,
  languageCode?: string,
): Promise<CustomPage | undefined> => {
  try {
    const pageResponse = await gqlClient.query<PageResponse>({
      query: GET_PAGES,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      variables: pagesVariables({
        pageType: 'page_listen',
        pageLayoutIdentifier: 'default_layout_listen_home',
        channelId,
        languageCode,
      }),
    });
    const pageData = pageResponse?.data;

    return pageData?.Pages.docs?.[0];
  } catch (error) {
    logger.error(`Error getting page: ${error}`);
  }
};
